/**
 * WP-V2-CS — Szkoła piesków: pokój interaktywny (PLAN-V2 §6 School).
 * Konsumuje: AnimatedDogAvatar, ObjectLayer, WorldCamera, WalkController.
 * Zachowuje hotspoty MgPuzzle / MgBubbles.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { worldBackButton } from '../../core/worldBackButton.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import { setupWorld, follow, WalkController } from '../../core/world/index.js';
import {
  SCHOOL_COLLIDERS,
  SCHOOL_OBJECT_DEFS,
  SCHOOL_PLACEMENTS,
  SCHOOL_SNAP_ZONES,
  SCHOOL_SHELF_SLOT_IDS,
  SCHOOL_WORLD_H,
  SCHOOL_WORLD_W,
} from './objects.js';
import { ensureSchoolTextures } from './schoolVisuals.js';
import {
  CHALK_COLORS,
  DEFAULT_BOARD,
  ERASER_ID,
  chalkColorById,
  isPointOnBoard,
  layoutChalkTools,
  type ChalkToolId,
} from './blackboard.js';
import {
  INTERACT_BUS_EVENT,
  createSchoolShelfProgress,
  recordSchoolShelfSnap,
  schoolMissionFromInteract,
  type SchoolShelfProgress,
} from './missionHook.js';

/** Klucz tekstury pieska-nauczycielki (dog_*_idle), z fallbackiem. */
function teacherDogTextureKey(scene: Phaser.Scene, breed: string): string | null {
  const primary = `dog_${breed}_idle`;
  if (scene.textures.exists(primary)) return primary;
  if (scene.textures.exists('dog_maltese_idle')) return 'dog_maltese_idle';
  if (scene.textures.exists('ph_dog')) return 'ph_dog';
  return null;
}

/** Jabłko na biurku nauczycielki — proceduralnie, bez emoji. */
function makeAppleBadge(scene: Phaser.Scene): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  g.fillStyle(0xef5350, 1);
  g.fillCircle(0, 2, 10);
  g.fillStyle(0x66bb6a, 1);
  g.fillEllipse(4, -8, 10, 6);
  g.lineStyle(2, 0x8d6e63, 1);
  g.lineBetween(0, -6, 0, -12);
  return g;
}

class TeacherNpc implements InteractTarget {
  x: number;
  y: number;
  isMoving = false;
  readonly body: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, x: number, y: number, breed = 'maltese') {
    this.scene = scene;
    this.x = x;
    this.y = y;
    const body = scene.add.circle(0, 0, 34, 0xffe4e1).setStrokeStyle(3, 0xffffff);
    const dogKey = teacherDogTextureKey(scene, breed);
    const face = dogKey
      ? scene.add.image(0, -2, dogKey).setDisplaySize(56, 56)
      : scene.add.circle(0, -2, 22, 0xffe0b2).setStrokeStyle(2, 0xffcc80);
    const apple = makeAppleBadge(scene);
    apple.setPosition(22, -20);
    const tag = scene.add
      .text(0, 48, 'Pani nauczycielka', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffcc',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5, 0);
    this.body = scene.add.container(x, y, [body, face, apple, tag]);
    this.body.setDepth(y + 2);
    this.body.setSize(72, 72);
    this.body.setInteractive(
      new Phaser.Geom.Rectangle(-40, -40, 80, 80),
      Phaser.Geom.Rectangle.Contains,
    );
    this.body.on('pointerdown', () => {
      playFx(this.scene, 'hearts', this.x, this.y - 40);
      void this.playAction('happy');
    });
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }
  moveTo(): void {}
  holdItem(): void {}
  async playAction(a: string): Promise<void> {
    const scale = a === 'happy' ? 1.2 : 1.1;
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.body,
        scaleX: scale,
        scaleY: scale,
        duration: 160,
        yoyo: true,
        onComplete: () => resolve(),
      });
    });
  }
  destroy(): void {
    this.body.destroy(true);
  }
}

export class SchoolScene extends BaseScene {
  static readonly KEY = 'School';

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private layer: ObjectLayer | null = null;
  private teacher: TeacherNpc | null = null;
  private unsubLayer: (() => void) | null = null;

  private boardGfx: Phaser.GameObjects.Graphics | null = null;
  private chalkTool: ChalkToolId = 'white';
  private drawing = false;
  private lastDraw: { x: number; y: number } | null = null;
  private chalkButtons: Phaser.GameObjects.Container[] = [];
  private suppliesSpawned = false;
  private shelfProgress: SchoolShelfProgress = createSchoolShelfProgress();
  private shelfProgressText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super(SchoolScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_school');
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    void preloadAnchors(this);
  }

  create(): void {
    setupWorld(this, {
      worldW: SCHOOL_WORLD_W,
      worldH: SCHOOL_WORLD_H,
      bg: 0xbde0fe,
    });
    // PNG z preload; gdy brak — proceduralne kształty (bez emoji-fallbacku)
    try {
      ensureSchoolTextures(this);
    } catch {
      /* headless */
    }
    this.drawClassroom();
    this.setupBlackboard();
    this.spawnPlayer();
    this.spawnTeacherAndLayer();
    this.spawnMinigameHotspots();
    this.spawnBackButton();

    this.events.once('shutdown', () => this.teardown());
    this.registry.set('schoolShelfProgress', { completed: 0, total: 4 });
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dt);
    const pos = this.walk?.position;
    this.avatar?.update(dt);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
      this.registry.set('companionFollow', { x: pos.x, y: pos.y });
    }
    this.layer?.update(dt);
  }

  private drawClassroom(): void {
    if (this.textures.exists('bg_school')) {
      this.add
        .image(SCHOOL_WORLD_W / 2, SCHOOL_WORLD_H / 2, 'bg_school')
        .setDisplaySize(SCHOOL_WORLD_W, SCHOOL_WORLD_H)
        .setDepth(-1000);
    }
    // tablica (tło pod Graphics)
    const b = DEFAULT_BOARD;
    this.add
      .rectangle(b.x, b.y, b.width, b.height, 0x2d6a4f)
      .setStrokeStyle(6, 0x8b5a2b)
      .setDepth(2);
    // półka na klocki (snap)
    this.add
      .rectangle(1100, 220, 250, 48, 0xc68642)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(3);
    this.add
      .text(1100, 190, 'Półka', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setDepth(4);
    SCHOOL_SHELF_SLOT_IDS.forEach((id, index) => {
      const letter = id.at(-1)!.toUpperCase();
      this.add
        .rectangle(1010 + index * 60, 220, 48, 40, 0xfff4cf, 0.78)
        .setStrokeStyle(2, 0xffffff)
        .setDepth(3.5);
      this.add
        .text(1010 + index * 60, 220, letter, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#8b5a2b',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(4);
    });
    this.shelfProgressText = this.add
      .text(1100, 264, 'Klocki: 0/4', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(4);

    this.add
      .text(SCHOOL_WORLD_W / 2, 36, 'Szkoła piesków', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
    this.add
      .text(SCHOOL_WORLD_W / 2, 72, 'Rysuj na tablicy · układaj klocki na półce', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
  }

  private setupBlackboard(): void {
    this.boardGfx = this.add.graphics().setDepth(5);
    // paleta kredek (UI fixed — scrollFactor 0), cele ≥64; ikony proceduralne (bez emoji)
    const tools = layoutChalkTools(40, SCHOOL_WORLD_H - 56, 10);
    for (const t of tools) {
      const fill = t.color ?? 0xffcdd2;
      const bg = this.add
        .rectangle(0, 0, t.size, t.size, fill, t.id === ERASER_ID ? 0.95 : 1)
        .setStrokeStyle(3, this.chalkTool === t.id ? 0x4a3f55 : 0xffffff);
      const icon = this.makeChalkIcon(t.id, t.color);
      const c = this.add.container(t.x + t.size / 2, t.y, [bg, icon]);
      c.setSize(t.size, t.size);
      c.setScrollFactor(0);
      c.setDepth(20_050);
      c.setInteractive(
        new Phaser.Geom.Rectangle(-t.size / 2, -t.size / 2, t.size, t.size),
        Phaser.Geom.Rectangle.Contains,
      );
      c.on('pointerdown', () => {
        this.chalkTool = t.id;
        this.refreshChalkSelection();
        playFx(this, t.id === ERASER_ID ? 'sparkle' : 'stars', t.x + t.size / 2, t.y);
      });
      c.setData('toolId', t.id);
      c.setName(`hotspot-chalk-${t.id}`);
      this.chalkButtons.push(c);
    }
    this.refreshChalkSelection();

    // rysowanie: pointer na tablicy (world space)
    this.input.on('pointerdown', this.onBoardPointerDown, this);
    this.input.on('pointermove', this.onBoardPointerMove, this);
    this.input.on('pointerup', this.onBoardPointerUp, this);
  }

  /** Ikona kredki/gumki — kolorowy kształt, zero emoji. */
  private makeChalkIcon(
    id: ChalkToolId,
    color: number | null,
  ): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();
    if (id === ERASER_ID) {
      g.fillStyle(0xf8bbd0, 1);
      g.fillRoundedRect(-16, -10, 32, 20, 5);
      g.fillStyle(0xffffff, 0.7);
      g.fillRoundedRect(-12, -4, 24, 8, 3);
    } else {
      const tip = color ?? 0xffffff;
      g.fillStyle(tip, 1);
      g.fillRoundedRect(-6, -18, 12, 32, 3);
      g.fillStyle(0xffffff, 0.85);
      g.fillRect(-6, 2, 12, 6);
      g.fillStyle(tip, 1);
      g.fillTriangle(-6, -18, 6, -18, 0, -28);
    }
    return g;
  }

  private refreshChalkSelection(): void {
    for (const c of this.chalkButtons) {
      const id = c.getData('toolId') as ChalkToolId;
      const bg = c.list[0] as Phaser.GameObjects.Rectangle;
      bg.setStrokeStyle(4, id === this.chalkTool ? 0x4a3f55 : 0xffffff);
      bg.setScale(id === this.chalkTool ? 1.08 : 1);
    }
  }

  private onBoardPointerDown(pointer: Phaser.Input.Pointer): void {
    const wp = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    if (!isPointOnBoard(wp.x, wp.y)) return;
    // nie kradnij klików obiektów interaktywnych pod palcem (poza tablicą OK)
    this.drawing = true;
    this.lastDraw = { x: wp.x, y: wp.y };
    this.strokeAt(wp.x, wp.y, wp.x, wp.y);
    this.game.events.emit(INTERACT_BUS_EVENT, {
      sceneKey: SchoolScene.KEY,
      type: 'draw',
      defId: 'school_blackboard',
      objectId: 'school_blackboard',
      x: wp.x,
      y: wp.y,
      tool: this.chalkTool,
    });
    this.events.emit('blackboard-draw', { x: wp.x, y: wp.y, tool: this.chalkTool });
  }

  private onBoardPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.drawing || !pointer.isDown) return;
    const wp = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    if (!isPointOnBoard(wp.x, wp.y)) return;
    const prev = this.lastDraw ?? wp;
    this.strokeAt(prev.x, prev.y, wp.x, wp.y);
    this.lastDraw = { x: wp.x, y: wp.y };
  }

  private onBoardPointerUp(): void {
    this.drawing = false;
    this.lastDraw = null;
  }

  private strokeAt(x0: number, y0: number, x1: number, y1: number): void {
    if (!this.boardGfx) return;
    if (this.chalkTool === ERASER_ID) {
      // gumka: zamaluj kolorem tablicy
      this.boardGfx.lineStyle(22, 0x2d6a4f, 1);
    } else {
      const col = chalkColorById(this.chalkTool) ?? CHALK_COLORS[0].color;
      this.boardGfx.lineStyle(6, col, 0.95);
    }
    this.boardGfx.beginPath();
    this.boardGfx.moveTo(x0, y0);
    this.boardGfx.lineTo(x1, y1);
    this.boardGfx.strokePath();
  }

  private spawnPlayer(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: 180,
      y: 520,
      breed,
      color,
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      depth: 100,
    });
    this.walk = new WalkController(this, this.avatar, {
      colliders: SCHOOL_COLLIDERS,
      bounds: {
        minX: 48,
        minY: 140,
        maxX: SCHOOL_WORLD_W - 48,
        maxY: SCHOOL_WORLD_H - 48,
      },
      enableInput: true,
    });
    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });
  }

  private spawnTeacherAndLayer(): void {
    const breed = (this.registry.get('dogBreed') as string) || 'maltese';
    this.teacher = new TeacherNpc(this, 180, 280, breed);
    const self = this;
    const playerTarget: InteractTarget = {
      moveTo: (x, y) => self.walk?.moveTo(x, y),
      holdItem: (tex) => self.avatar?.holdItem(tex),
      playAction: (a) => {
        const action = a as 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy' | 'sniff';
        if (action === 'sniff') return self.avatar?.playAction('happy') ?? Promise.resolve();
        return self.avatar?.playAction(action) ?? Promise.resolve();
      },
      getPosition: () =>
        self.walk?.position ?? { x: self.avatar?.x ?? 0, y: self.avatar?.y ?? 0 },
      get isMoving() {
        return self.walk?.isMoving ?? false;
      },
      get x() {
        return self.walk?.position.x ?? self.avatar?.x ?? 0;
      },
      get y() {
        return self.walk?.position.y ?? self.avatar?.y ?? 0;
      },
    };

    this.layer = new ObjectLayer({
      scene: this,
      defs: SCHOOL_OBJECT_DEFS,
      target: playerTarget,
      giveTargets: this.teacher ? [this.teacher] : [],
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: SCHOOL_WORLD_W - 48,
        minY: 360,
        maxY: SCHOOL_WORLD_H - 48,
      },
    });
    this.layer.setSnapZones(SCHOOL_SNAP_ZONES);
    this.layer.spawn(SCHOOL_PLACEMENTS);
    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private onInteract(evt: InteractEvent): void {
    this.game.events.emit(INTERACT_BUS_EVENT, {
      sceneKey: SchoolScene.KEY,
      ...evt,
    });
    this.events.emit('interact', evt);

    const mission = schoolMissionFromInteract(evt);
    if (mission) {
      const next = recordSchoolShelfSnap(
        this.shelfProgress,
        evt.defId,
        evt.snapZoneId ?? '',
      );
      if (next === this.shelfProgress) return;
      const justCompleted = !this.shelfProgress.done && next.done;
      this.shelfProgress = next;
      this.shelfProgressText?.setText(
        next.done ? 'Klocki: 4/4 — gotowe!' : `Klocki: ${next.completed.length}/4`,
      );
      this.registry.set('schoolShelfProgress', {
        completed: next.completed.length,
        total: 4,
      });
      this.game.events.emit(INTERACT_BUS_EVENT, mission);
      this.events.emit('quest-progress', mission);
      playFx(this, 'stars', evt.x, evt.y);
      if (justCompleted) {
        playFx(this, 'stars', 1100, 210);
        playFx(this, 'hearts', 1100, 240);
        this.floatHint(1100, 280, 'Brawo! Cała półka gotowa!');
        void this.avatar?.playAction('happy');
      }
    }

    if (evt.type === 'tap' || evt.type === 'toggle') {
      if (evt.defId === 'school_backpack') this.openBackpack(evt.x, evt.y);
      if (evt.defId === 'school_bell') this.ringBell(evt.x, evt.y);
      if (evt.defId === 'school_desk_a' || evt.defId === 'school_desk_b') {
        void this.avatar?.playAction('sit');
        playFx(this, 'hearts', evt.x, evt.y - 20);
      }
      if (evt.defId === 'school_globe') {
        playFx(this, 'sparkle', evt.x, evt.y);
        void this.avatar?.playAction('happy');
        this.floatHint(evt.x, evt.y, 'Globus kręci się!');
      }
    }
  }

  private floatHint(x: number, y: number, text: string): void {
    const t = this.add
      .text(x, y - 40, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: t,
      y: y - 80,
      alpha: 0,
      duration: 800,
      onComplete: () => t.destroy(),
    });
  }

  private openBackpack(x: number, y: number): void {
    playFx(this, 'stars', x, y);
    if (this.suppliesSpawned || !this.layer) return;
    this.suppliesSpawned = true;
    // wyjmij dodatkowe przybory (placeholdery)
    this.layer.spawn([
      { defId: 'school_crayon', x: x + 50, y: y + 20 },
      { defId: 'school_eraser', x: x + 90, y: y + 30 },
      { defId: 'school_crayon', x: x + 70, y: y + 60 },
    ]);
    const hint = this.add
      .text(x, y - 50, 'Przybory!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: hint,
      y: y - 90,
      alpha: 0,
      duration: 900,
      onComplete: () => hint.destroy(),
    });
  }

  private ringBell(x: number, y: number): void {
    playFx(this, 'notes', x, y);
    const t = this.add
      .text(x, y - 40, 'dzwonek!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6a1b9a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: t,
      y: y - 80,
      alpha: 0,
      duration: 700,
      onComplete: () => t.destroy(),
    });
  }

  private spawnMinigameHotspots(): void {
    const puzzle = UiKit.button(this, {
      x: SCHOOL_WORLD_W / 2 - 160,
      y: SCHOOL_WORLD_H - 56,
      label: '🧩 Puzzle',
      width: 220,
      height: Math.max(72, MIN_TOUCH),
      fill: 0xc9b1ff,
      onClick: () => getSceneRouter().go('MgPuzzle'),
    });
    puzzle.setScrollFactor(0);
    puzzle.setDepth(20_100);
    puzzle.setData('navTarget', 'MgPuzzle');
    puzzle.setName('hotspot-MgPuzzle');

    const bubbles = UiKit.button(this, {
      x: SCHOOL_WORLD_W / 2 + 160,
      y: SCHOOL_WORLD_H - 56,
      label: '🫧 Bąbelki',
      width: 220,
      height: Math.max(72, MIN_TOUCH),
      fill: 0x90e0ef,
      onClick: () => getSceneRouter().go('MgBubbles'),
    });
    bubbles.setScrollFactor(0);
    bubbles.setDepth(20_100);
    bubbles.setData('navTarget', 'MgBubbles');
    bubbles.setName('hotspot-MgBubbles');
  }

  private spawnBackButton(): void {
    worldBackButton(this);
  }

  private teardown(): void {
    this.input.off('pointerdown', this.onBoardPointerDown, this);
    this.input.off('pointermove', this.onBoardPointerMove, this);
    this.input.off('pointerup', this.onBoardPointerUp, this);
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.teacher?.destroy();
    this.teacher = null;
    this.boardGfx?.destroy();
    this.boardGfx = null;
    this.chalkButtons = [];
    this.shelfProgressText = null;
    this.registry.remove('schoolShelfProgress');
    this.registry.remove('companionFollow');
  }
}

export default SchoolScene;
